import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LoginCont, WelcomeMessage } from './Login.styles';
import SignInForm from '../../components/SignInForm/SignInForm';
import { getErrorDesc } from '../../helpers/helpers';
import { useAuth } from '../../hooks/useAuth';
import { ILoginRequest } from '../../services/auth';

interface IApiError {
  title: string | null;
  description: string | null;
}

const apiErrorInit = { title: null, description: null };

const Login: FC = () => {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<IApiError>(apiErrorInit);
  const { t } = useTranslation();
  const { login, userData, getRouteFromBaskets, handleMicrosoftLogin } = useAuth();
  const doLogin = async (credentials: ILoginRequest) => {
    try {
      await login(credentials);
      setApiError(apiErrorInit);
    } catch (e) {
      const errorDesc = getErrorDesc(e?.data);
      setApiError(errorDesc);
    }
  };

  useEffect(() => {
    const hash = window.location.hash;
    const queryParams = new URLSearchParams(hash.substring(1));
    const token = queryParams.get('access_token');
    const error = queryParams.get('error');
    const errorDescription = queryParams.get('error_description');

    if (token || error) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (error) {
      const knownErrors: Record<string, { title: string; description: string }> = {
        consent_required: {
          title: t('login.signIn.microsoftErrors.consent_required.title'),
          description: t('login.signIn.microsoftErrors.consent_required.description'),
        },
        access_denied: {
          title: t('login.signIn.microsoftErrors.access_denied.title'),
          description: t('login.signIn.microsoftErrors.access_denied.description'),
        },
        login_required: {
          title: t('login.signIn.microsoftErrors.login_required.title'),
          description: t('login.signIn.microsoftErrors.login_required.description'),
        },
        interaction_required: {
          title: t('login.signIn.microsoftErrors.interaction_required.title'),
          description: t('login.signIn.microsoftErrors.interaction_required.description'),
        },
      };
      setApiError(
        knownErrors[error] ?? {
          title: t('login.signIn.microsoftErrors.unknown.title'),
          description: errorDescription || error,
        }
      );
    } else if (token) {
      handleMicrosoftLogin(token).catch((e) => {
        const errorDesc = getErrorDesc(e?.data);
        setApiError(errorDesc);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = userData?.access;

    if (token) {
      const route = getRouteFromBaskets(token);
      navigate(route);
    }
  }, []);

  return (
    <LoginCont>
      <WelcomeMessage>{t('login.signIn.welcomeMessage')}</WelcomeMessage>
      <SignInForm submitForm={doLogin} apiError={apiError} />
    </LoginCont>
  );
};

export default Login;
